import React from 'react';
import {
    Box,
    TableCell,
    Typography,
    makeStyles,
    Theme,
    createStyles,
    IconButton,
    TableRow,
    useTheme,
    useMediaQuery
} from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxTaped, faXmark, faRefresh } from '@fortawesome/pro-regular-svg-icons';
import { Membership, MembershipItem } from '@spike/membership-model';
import { useCommonStyles as useTableStyles } from './Table/CommonStyles';
import Table from './Table';
import clsx from 'clsx';

interface MembershipDetailsModalProps {
    open: boolean;
    onClose: () => void;
    membership: Membership;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        modal: {
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: '#fff',
            padding: theme.spacing(4),
            zIndex: 10010,
            width: '80%',
            maxWidth: 675,
            boxShadow: theme.shadows[5],
            borderRadius: 30,
            [theme.breakpoints.down('sm')]: {
                top: 0,
                left: 0,
                transform: 'none',
                width: '100%',
                maxWidth: '100%',
                height: '100%',
                borderRadius: 0,
                boxShadow: 'none',
                padding: 20,
                overflowY: 'auto'
            }
        },
        backdrop: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 10010
        },
        header: {
            paddingTop: 25,
            marginBottom: 15,
            display: 'block'
        },
        tableContainer: {
            marginTop: theme.spacing(2)
        },
        total: {
            marginTop: theme.spacing(2),
            display: 'flex',
            justifyContent: 'flex-end',
            fontWeight: 600
        },
        cellName: {
            width: '300px',
            maxWidth: '300px',
            wordBreak: 'break-word',
            whiteSpace: 'normal',
            overflowWrap: 'break-word',
            [theme.breakpoints.down('sm')]: {
                width: '200px',
                maxWidth: '200px'
            }
        },
        headerCellSlim: {
            '&:first-child': {
                paddingLeft: 0
            },
            '&:last-child': {
                paddingRight: 0
            }
        },
        cellSlim: {
            '&:first-child': {
                paddingLeft: 0
            },
            '&:last-child': {
                paddingRight: 0
            }
        },
        rightAlign: {
            textAlign: 'right'
        },
        closeButton: {
            position: 'absolute',
            top: 20,
            right: 20,
            color: '#000',
            [theme.breakpoints.down('sm')]: {
                top: 10,
                right: 10
            }
        },
        headerTitle: {
            fontSize: 20,
            fontWeight: 600,
            color: '#000'
        },
        subscriptionType: {
            marginBottom: 15,
            display: 'flex',
            gap: 40,
            paddingTop: 25,
            paddingBottom: 0
        },
        itemType: {
            fontSize: 14,
            fontWeight: 400,
            color: '#7A7A7A',
            marginTop: 5,
            display: 'block'
        },
        subscriptionTypeItem: {
            position: 'relative'
        },
        subscriptionTypeItemLabel: {
            fontSize: 16,
            fontWeight: 600,
            color: '#000',
            marginBottom: 5
        },
        subscriptionTypeItemValue: {
            fontSize: 16,
            fontWeight: 400
        },
        subscriptionTypeItemIcon: {
            marginRight: 4
        },
        mobileItemLabel: {
            [theme.breakpoints.down('sm')]: {
                marginBottom: 0
            }
        },
        mobileItemType: {
            [theme.breakpoints.down('sm')]: {
                marginTop: '-1px',
                marginBottom: '8px'
            }
        },
        totalValue: {
            fontWeight: 400,
            marginLeft: 5
        }
    })
);

const MembershipDetailsModal: React.FC<MembershipDetailsModalProps> = ({ open, onClose, membership }) => {
    const classes = useStyles();
    const tableStyles = useTableStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    if (!open) return null;

    const headersMembershipDetail = (
        <>
            <TableCell className={clsx(tableStyles.headerCell, classes.headerCellSlim)}>Item</TableCell>
            <TableCell className={clsx(tableStyles.headerCell, classes.headerCellSlim, classes.rightAlign)}>
                Price
            </TableCell>
            <TableCell className={clsx(tableStyles.headerCell, classes.headerCellSlim, classes.rightAlign)}>
                Quantity
            </TableCell>
            <TableCell className={clsx(tableStyles.headerCell, classes.headerCellSlim, classes.rightAlign)}>
                Total
            </TableCell>
        </>
    );

    const rowsMembershipDetail = (
        <>
            {membership.membershipItems?.map(item => (
                <TableRow key={item.id}>
                    <TableCell className={clsx(tableStyles.cell, classes.cellName, classes.cellSlim)}>
                        {item.itemName}
                        <Typography className={classes.itemType}>{item.itemType}</Typography>
                    </TableCell>
                    <TableCell className={clsx(tableStyles.cell, classes.cellSlim, classes.rightAlign)}>
                        ${Number(item.amount).toFixed(2)}
                    </TableCell>
                    <TableCell className={clsx(tableStyles.cell, classes.cellSlim, classes.rightAlign)}>
                        {item.unlimited ? 'Unlimited' : item.quantity}
                    </TableCell>
                    <TableCell className={clsx(tableStyles.cell, classes.cellSlim, classes.rightAlign)}>
                        ${Number(item.total).toFixed(2)}
                    </TableCell>
                </TableRow>
            ))}
        </>
    );

    const MobileItemDetail = ({ item }: { item: MembershipItem }) => (
        <Box
            mb={'10px'}
            borderBottom="1px solid #e0e0e0"
        >
            <Typography
                variant="h6"
                className={clsx(classes.subscriptionTypeItemLabel, classes.mobileItemLabel)}
            >
                {item.itemName}
            </Typography>
            <Typography className={clsx(classes.itemType, classes.mobileItemType)}>{item.itemType}</Typography>
            <Box
                display="flex"
                justifyContent="space-between"
                mt={'5px'}
                mb={'5px'}
            >
                <Box
                    fontSize={'14px'}
                    fontWeight={500}
                >
                    Qty
                </Box>
                <Box
                    fontSize={'14px'}
                    fontWeight={400}
                >
                    {item.unlimited ? 'Unlimited' : item.quantity}
                </Box>
            </Box>
            <Box
                display="flex"
                justifyContent="space-between"
                mb={'5px'}
            >
                <Box
                    fontSize={'14px'}
                    fontWeight={500}
                >
                    Price
                </Box>
                <Box
                    fontSize={'14px'}
                    fontWeight={400}
                >
                    ${Number(item.amount).toFixed(2)}
                </Box>
            </Box>
            <Box
                display="flex"
                justifyContent="space-between"
                mb={'14px'}
            >
                <Box
                    fontSize={'14px'}
                    fontWeight={500}
                >
                    Total
                </Box>
                <Box
                    fontSize={'14px'}
                    fontWeight={400}
                >
                    ${Number(item.total).toFixed(2)}
                </Box>
            </Box>
        </Box>
    );

    const mobileMembershipDetailTable = (
        <Box>
            {membership.membershipItems?.map((item, index) => (
                <MobileItemDetail
                    key={index}
                    item={item}
                />
            ))}
            <Typography className={classes.total}>
                Total: <Typography className={classes.totalValue}>${Number(membership.total).toFixed(2)}</Typography>
            </Typography>
        </Box>
    );

    const desktopMembershipDetailTable = (
        <>
            <Box className={classes.tableContainer}>
                <Table
                    headers={headersMembershipDetail}
                    rows={rowsMembershipDetail}
                    loading={false}
                    withoutColor={true}
                />
            </Box>
            <Typography className={classes.total}>
                Total: <Typography className={classes.totalValue}>${Number(membership.total).toFixed(2)}</Typography>
            </Typography>
        </>
    );

    const renderMembershipDetailTable = () => {
        return isMobile ? mobileMembershipDetailTable : desktopMembershipDetailTable;
    };

    return (
        <>
            <Box
                className={classes.backdrop}
                onClick={onClose}
            />
            <Box className={classes.modal}>
                <Box className={classes.header}>
                    <Typography className={classes.headerTitle}>Membership Details</Typography>
                    <Box className={classes.subscriptionType}>
                        {membership.enrollmentType === 'subscription' && (
                            <>
                                <Box className={classes.subscriptionTypeItem}>
                                    <Typography className={classes.subscriptionTypeItemLabel}>Type</Typography>
                                    <Typography className={classes.subscriptionTypeItemValue}>
                                        <FontAwesomeIcon
                                            className={classes.subscriptionTypeItemIcon}
                                            icon={faRefresh}
                                        />
                                        Subscription
                                    </Typography>
                                </Box>
                                <Box className={classes.subscriptionTypeItem}>
                                    <Typography className={classes.subscriptionTypeItemLabel}>Frequency</Typography>
                                    {membership.billingFrequency && (
                                        <Typography className={classes.subscriptionTypeItemValue}>
                                            {membership.billingFrequency.charAt(0).toUpperCase() +
                                                membership.billingFrequency.slice(1)}
                                        </Typography>
                                    )}
                                </Box>
                            </>
                        )}
                        {membership.enrollmentType === 'package' && (
                            <Box className={classes.subscriptionTypeItem}>
                                <Typography className={classes.subscriptionTypeItemLabel}>Type</Typography>
                                <Typography className={classes.subscriptionTypeItemValue}>
                                    <FontAwesomeIcon
                                        className={classes.subscriptionTypeItemIcon}
                                        icon={faBoxTaped}
                                    />
                                    Package
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    <IconButton
                        className={classes.closeButton}
                        onClick={onClose}
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </IconButton>
                </Box>

                {renderMembershipDetailTable()}
            </Box>
        </>
    );
};

export default MembershipDetailsModal;
